/**
 * Contacts list.
 */

import { Form, Link, useLoaderData, useNavigation, useSearchParams } from 'react-router-dom'
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router-dom'
import type { Contact, ContactListResult } from '@erp/contracts'
import { api } from '../api'
import type { FieldRegistry, SavedView } from '@erp/contracts'
import { usePagedRows } from '../paging'
import { filtersParam } from '../view-state'
import { ViewBar, handleViewIntent } from '../components/view-bar'
import { Button, EmptyState, PageHeader, SearchInput, Spinner, Table, Toolbar } from '../ui'
import { contactName, formatDate } from '../format'

interface ListData {
  page: ContactListResult
  registry: FieldRegistry
  views: SavedView[]
}

async function fetchPage(request: Request): Promise<ContactListResult> {
  const url = new URL(request.url)
  return api.contacts.list({
    search: url.searchParams.get('q') ?? undefined,
    organizationId: url.searchParams.getAll('organization'),
    includeArchived: url.searchParams.get('archived') === 'true',
    filters: filtersParam(url.searchParams),
    cursor: url.searchParams.get('cursor') ?? undefined,
    limit: 50,
  })
}

/** Fetcher-only route: just the page, which is what usePagedRows expects. */
export const pageLoader = ({ request }: LoaderFunctionArgs) => fetchPage(request)

export async function loader({ request }: LoaderFunctionArgs): Promise<ListData> {
  const [page, registry, views] = await Promise.all([
    fetchPage(request),
    api.registry('contact'),
    api.views.forEntity('contact'),
  ])
  return { page, registry, views }
}

export async function action({ request }: ActionFunctionArgs) {
  const form = await request.formData()
  const result = await handleViewIntent(String(form.get('intent')), form)
  return result ?? { ok: false, message: 'Unknown action' }
}

export default function Contacts() {
  const { page, registry, views } = useLoaderData() as ListData
  const navigation = useNavigation()
  const [searchParams] = useSearchParams()
  const { rows, cursor, loading, loadMore } = usePagedRows(page, '/crm/contacts/page')

  return (
    <>
      <PageHeader
        title="Contacts"
        actions={
          <Link to="/crm/contacts/new" className="ax-btn ax-btn-primary">
            Create
          </Link>
        }
      />

      <ViewBar entity="contact" registry={registry} views={views} />

      <Toolbar>
        <Form method="get" role="search">
          <SearchInput
            name="q"
            defaultValue={searchParams.get('q') ?? ''}
            placeholder="Search contacts"
          />
        </Form>
        <span className="ax-toolbar-count">
          {page.totalCount} {page.totalCount === 1 ? 'contact' : 'contacts'}
        </span>
        {navigation.state === 'loading' ? <Spinner /> : null}
      </Toolbar>

      {rows.length === 0 ? (
        <EmptyState title="No contacts match this view" />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Phone</th>
                <th scope="col">Job title</th>
                <th scope="col">Last modified</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((contact) => (
                <tr key={contact.id}>
                  <td>
                    <Link to={`/crm/contacts/${contact.id}`}>{contactName(contact)}</Link>
                  </td>
                  <td>{contact.email ?? '—'}</td>
                  <td>{contact.phone ?? contact.mobile ?? '—'}</td>
                  <td>{contact.jobTitle ?? '—'}</td>
                  <td>{formatDate(contact.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          {cursor ? (
            <div className="ax-list-footer">
              <Button onClick={loadMore} disabled={loading}>
                {loading ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </>
  )
}


